import React, { useState, useEffect } from 'react';
import userService from '../../services/userService';
import api from '../../config/axiosConfig';
import { localTodayIso } from '../../utils/localDate';

/**
 * Landing page del portal de cliente (usuario INVITADO), mostrada justo después
 * de iniciar sesión en vez del Dashboard interno (Home). Es una página independiente
 * (no usa MainLayout/Navbar) con su propio encabezado, a propósito más simple y
 * enfocada en lo que le importa a un representante de empresa: su cumplimiento.
 *
 * Todos los datos (cotizaciones, % de avance de PIPC/RIC, documentos, capacitación,
 * próxima inspección) se calculan a partir de los mismos endpoints que usa el resto
 * de la plataforma (perfil-riesgo/historial, Home, Programas), para que la información
 * sea consistente en toda la aplicación.
 */
const PortalInvitado = () => {
  const [userName, setUserName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [loading, setLoading] = useState(true);
  const [hasCompany, setHasCompany] = useState(true);

  const [quotationStats, setQuotationStats] = useState({ total: 0, pending: 0, accepted: 0 });
  const [pendingQuotations, setPendingQuotations] = useState([]);
  const [validation, setValidation] = useState(null); // { matriz, branches, totalEntities }
  const [nextInspection, setNextInspection] = useState(null);
  const [nextTraining, setNextTraining] = useState(null);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        const user = await userService.getCurrentUser();
        if (!mounted || !user) return;

        setUserName(user.firstName || '');
        setCompanyName(user.company?.businessName || '');

        const companyId = user.company?.id;
        if (!companyId) {
          setHasCompany(false);
          setLoading(false);
          return;
        }

        const [quotationsRes, workOrdersRes, upcomingRes] = await Promise.allSettled([
          api.get('/quotations'),
          api.get(`/work-orders/by-company/${companyId}`),
          api.get(`/work-order-details/by-company/${companyId}`),
        ]);

        if (!mounted) return;

        // Cotizaciones — mismo endpoint y filtro que perfil-riesgo/historial (Historial.jsx)
        const quotations = (quotationsRes.status === 'fulfilled' && Array.isArray(quotationsRes.value.data)
          ? quotationsRes.value.data : []).filter(q => q.companyId === companyId);
        const pending = quotations.filter(q => q.status === 'PENDING' || q.status === 'DRAFT');
        const accepted = quotations.filter(q => q.status === 'ACCEPTED');
        setQuotationStats({ total: quotations.length, pending: pending.length, accepted: accepted.length });
        setPendingQuotations(pending);

        // Próximas inspecciones — mismo endpoint y filtro que usa Home para el invitado
        if (upcomingRes.status === 'fulfilled' && Array.isArray(upcomingRes.value.data) && upcomingRes.value.data.length > 0) {
          setNextInspection(upcomingRes.value.data[0]);
        }

        // Orden de trabajo activa (la más reciente) → validación real de PIPC/RIC + capacitación
        const workOrders = workOrdersRes.status === 'fulfilled' && Array.isArray(workOrdersRes.value.data)
          ? workOrdersRes.value.data : [];
        if (workOrders.length > 0) {
          const activeWorkOrder = [...workOrders].sort((a, b) => b.id - a.id)[0];

          const [validationRes, trainingsRes] = await Promise.allSettled([
            api.get(`/files/work-order/${activeWorkOrder.id}/validate`),
            api.get(`/trainings/work-order/${activeWorkOrder.id}`),
          ]);

          if (mounted && validationRes.status === 'fulfilled') {
            setValidation(validationRes.value.data);
          }

          if (mounted && trainingsRes.status === 'fulfilled') {
            const trainings = Array.isArray(trainingsRes.value.data) ? trainingsRes.value.data : [];
            const today = localTodayIso();
            const upcoming = trainings
              .filter(t => t.trainingDate >= today)
              .sort((a, b) => a.trainingDate.localeCompare(b.trainingDate));
            setNextTraining(upcoming[0] || trainings[0] || null);
          }
        }
      } catch (err) {
        console.error('Error cargando datos del portal:', err);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => { mounted = false; };
  }, []);

  const initials = (companyName || 'CN')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join('') || 'CN';

  // --- Derivados de la validación real (RIC / PIPC) ---
  const entities = validation ? [validation.matriz, ...(validation.branches || [])].filter(Boolean) : [];
  const avgPct = (docKey) => {
    if (entities.length === 0) return null;
    const sum = entities.reduce((acc, e) => acc + (e[docKey]?.completionPercentage ?? 0), 0);
    return Math.round(sum / entities.length);
  };
  const isReady = (docKey) => entities.length > 0 && entities.every(e => e[docKey]?.ready);
  const pipcPct = avgPct('pipc');
  const ricPct = avgPct('ric');

  // Documentos de cumplimiento (Subir Archivos) — mismo dato real que usa Programas.jsx
  // para el staff, agregado entre matriz y todas las sucursales de la empresa.
  const uploadedDocsTotal = entities.reduce((acc, e) => acc + (e.documentos?.uploadedCount ?? 0), 0);
  const requiredDocsTotal = entities.reduce((acc, e) => acc + (e.documentos?.totalRequired ?? 0), 0);

  // Checklist real: campos requeridos faltantes, combinando PIPC+RIC de todas las entidades
  const missingItems = [];
  entities.forEach(e => {
    ['ric', 'pipc'].forEach(docKey => {
      (e[docKey]?.missingRequired || []).forEach(field => {
        const dedupeKey = `${e.entityName}-${field.key}`;
        if (!missingItems.some(m => m.dedupeKey === dedupeKey)) {
          missingItems.push({ dedupeKey, title: field.description, meta: e.entityName });
        }
      });
    });
    (e.documentos?.missingDocuments || []).forEach(doc => {
      const dedupeKey = `${e.entityName}-doc-${doc.key}`;
      if (!missingItems.some(m => m.dedupeKey === dedupeKey)) {
        missingItems.push({ dedupeKey, title: `Subir: ${doc.label}`, meta: e.entityName });
      }
    });
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(`${dateStr}T00:00:00`);
    if (isNaN(d.getTime())) return null;
    return { day: d.getDate(), mon: d.toLocaleDateString('es-MX', { month: 'short' }).replace('.', '') };
  };
  const inspectionDate = nextInspection ? formatDate(nextInspection.scheduledDate) : null;

  if (loading) {
    return (
      <div className="portal-invitado">
        <style>{PORTAL_STYLES}</style>
        <div className="loading-screen">Cargando…</div>
      </div>
    );
  }

  return (
    <div className="portal-invitado">
      <style>{PORTAL_STYLES}</style>

      <div className="topbar">
        <div className="brand">
          <img src="/logo.jpg" alt="CONEM" />
          <div className="divider" />
          <span className="portal-tag">Portal del cliente</span>
        </div>
        <div className="topbar-right">
          <span>{companyName || '—'}</span>
          <div className="avatar">{initials}</div>
        </div>
      </div>

      <div className="hero">
        <div className="hero-inner">
          <div className="label">Resumen de cumplimiento</div>
          <h1>{userName ? `Buenos días, ${userName}` : 'Buenos días'}</h1>
          <p className="sub">
            {companyName
              ? `Este es el estado actual del cumplimiento de Protección Civil de ${companyName}.`
              : 'Este es el estado actual de tu cumplimiento de Protección Civil.'}
          </p>
        </div>
      </div>

      <div className="content">
        {!hasCompany ? (
          <div className="panel">
            <p style={{ margin: 0 }}>Tu usuario no tiene una empresa asociada. Contacta a tu ejecutivo de cuenta.</p>
          </div>
        ) : quotationStats.total === 0 ? (
          <div className="panel">
            <p style={{ margin: 0 }}>Aún no tienes cotizaciones registradas. Cuando tu ejecutivo genere una, aparecerá aquí.</p>
          </div>
        ) : (
          <>
            <div className="status-grid">
              <div className={`status-card ${pipcPct === null ? 'c-blue' : isReady('pipc') ? 'c-green' : 'c-red'}`}>
                <div className="top-row">
                  <div className={`icon ${pipcPct === null ? 'blue' : isReady('pipc') ? 'green' : 'red'}`}>📋</div>
                  <span className={`pill ${pipcPct === null ? 'info' : isReady('pipc') ? 'ok' : 'warn'}`}>
                    {pipcPct === null ? 'Sin orden activa' : isReady('pipc') ? 'Listo' : 'En proceso'}
                  </span>
                </div>
                <div className="figure">{pipcPct === null ? '—' : <>{pipcPct}<small>%</small></>}</div>
                <div className="caption">Programa Interno (PIPC)</div>
                <div className="bar-track"><div className={`bar-fill ${isReady('pipc') ? 'green' : 'red'}`} style={{ width: `${pipcPct ?? 0}%` }} /></div>
              </div>

              <div className={`status-card ${ricPct === null ? 'c-blue' : isReady('ric') ? 'c-green' : 'c-red'}`}>
                <div className="top-row">
                  <div className={`icon ${ricPct === null ? 'blue' : isReady('ric') ? 'green' : 'red'}`}>✅</div>
                  <span className={`pill ${ricPct === null ? 'info' : isReady('ric') ? 'ok' : 'warn'}`}>
                    {ricPct === null ? 'Sin orden activa' : isReady('ric') ? 'Listo' : 'En proceso'}
                  </span>
                </div>
                <div className="figure">{ricPct === null ? '—' : <>{ricPct}<small>%</small></>}</div>
                <div className="caption">Reporte de Inspección (RIC)</div>
                <div className="bar-track"><div className={`bar-fill ${isReady('ric') ? 'green' : 'red'}`} style={{ width: `${ricPct ?? 0}%` }} /></div>
              </div>

              <div className={`status-card ${requiredDocsTotal === 0 ? 'c-blue' : uploadedDocsTotal === requiredDocsTotal ? 'c-green' : 'c-red'}`}>
                <div className="top-row">
                  <div className={`icon ${requiredDocsTotal === 0 ? 'blue' : uploadedDocsTotal === requiredDocsTotal ? 'green' : 'red'}`}>📎</div>
                  <span className={`pill ${requiredDocsTotal === 0 ? 'info' : uploadedDocsTotal === requiredDocsTotal ? 'ok' : 'warn'}`}>
                    {requiredDocsTotal === 0 ? 'Sin orden activa' : uploadedDocsTotal === requiredDocsTotal ? 'Completo' : 'Faltan'}
                  </span>
                </div>
                <div className="figure">{requiredDocsTotal === 0 ? '—' : `${uploadedDocsTotal} / ${requiredDocsTotal}`}</div>
                <div className="caption">Documentos subidos</div>
                {requiredDocsTotal > 0 && (
                  <div className="bar-track"><div className={`bar-fill ${uploadedDocsTotal === requiredDocsTotal ? 'green' : 'red'}`} style={{ width: `${Math.round((uploadedDocsTotal / requiredDocsTotal) * 100)}%` }} /></div>
                )}
              </div>

              <div className="status-card c-blue">
                <div className="top-row">
                  <div className="icon blue">🎓</div>
                  <span className="pill info">{nextTraining ? 'Programada' : 'Sin programar'}</span>
                </div>
                <div className="figure" style={{ fontSize: nextTraining ? '1.3rem' : '2rem' }}>
                  {nextTraining ? nextTraining.trainingDate : '—'}
                </div>
                <div className="caption">Próxima capacitación</div>
              </div>
            </div>

            <div className="split">
              <div className="panel">
                <div className="panel-head">
                  <h2>Pendientes por resolver</h2>
                  <a className="link" href="/perfil-riesgo/llenar-informacion">Ver todo →</a>
                </div>
                <div className="checklist">
                  {pendingQuotations.map(q => (
                    <div className="check-row" key={`q-${q.id}`}>
                      <div className="check-dot pending">!</div>
                      <div className="check-text">
                        <div className="title">Cotización {q.invoiceFolio || q.folio || `#${q.id}`} pendiente de aceptar</div>
                        <div className="meta">Acéptala para continuar con el proceso</div>
                      </div>
                    </div>
                  ))}
                  {missingItems.slice(0, 5).map((item, idx) => (
                    <div className="check-row" key={item.dedupeKey}>
                      <div className="check-dot pending">{idx + 1}</div>
                      <div className="check-text">
                        <div className="title">{item.title}</div>
                        <div className="meta">{item.meta}</div>
                      </div>
                    </div>
                  ))}
                  {pendingQuotations.length === 0 && missingItems.length === 0 && (
                    <div className="check-row">
                      <div className="check-dot done">✓</div>
                      <div className="check-text">
                        <div className="title">No hay pendientes por resolver</div>
                        <div className="meta">Toda la información requerida está completa</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="panel">
                <div className="panel-head">
                  <h2>Accesos rápidos</h2>
                </div>
                <div className="action-list">
                  <a className="action" href="/perfil-riesgo/llenar-informacion">
                    <div className="glyph">📄</div>
                    <div className="txt">Llenar información de mi empresa</div>
                    <div className="arrow">→</div>
                  </a>
                  <a className="action" href="/perfil-riesgo/subir-archivos">
                    <div className="glyph">⬆</div>
                    <div className="txt">Subir documentos faltantes</div>
                    <div className="arrow">→</div>
                  </a>
                  <a className="action" href="/perfil-riesgo/historial">
                    <div className="glyph">📊</div>
                    <div className="txt">Ver historial de cotizaciones</div>
                    <div className="arrow">→</div>
                  </a>
                  <a className="action" href="/profile">
                    <div className="glyph">🏢</div>
                    <div className="txt">Actualizar datos de mi empresa</div>
                    <div className="arrow">→</div>
                  </a>
                </div>
              </div>
            </div>

            {nextInspection && inspectionDate && (
              <div className="inspection-banner">
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <div className="date-block">
                    <div className="day">{inspectionDate.day}</div>
                    <div className="mon">{inspectionDate.mon}</div>
                  </div>
                  <div>
                    <div className="label" style={{ color: '#9fb3bd' }}>Próxima inspección programada</div>
                    <div style={{ fontWeight: 800, marginTop: 2 }}>
                      {nextInspection.entityName}
                      {nextInspection.scheduledTimeStart ? ` · ${nextInspection.scheduledTimeStart}` : ''}
                    </div>
                  </div>
                </div>
                <a className="btn" href="/perfil-riesgo/historial">Ver detalles</a>
              </div>
            )}
          </>
        )}
      </div>

      <footer>CONEM — Consejo Organizacional de Empresas</footer>
    </div>
  );
};

const PORTAL_STYLES = `
  .portal-invitado {
    --ink: #0E2C3D;
    --ink-soft: #6b6f76;
    --paper: #faf9f7;
    --paper-card: #ffffff;
    --line: #e7e4de;
    --blue: #17ACE4;
    --blue-dark: #0e86b6;
    --blue-100: #e4f6fd;
    --green: #1CA65B;
    --green-dark: #158247;
    --green-100: #e3f6ea;
    --red: #E5343E;
    --red-dark: #b8232b;
    --red-100: #fce8e9;
    --radius: 14px;
    --shadow: 0 1px 2px rgba(14,44,61,0.04), 0 10px 28px rgba(14,44,61,0.08);
    background: var(--paper);
    color: var(--ink);
    font-family: -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
    -webkit-font-smoothing: antialiased;
    min-height: 100vh;
  }
  .portal-invitado * { box-sizing: border-box; }
  .portal-invitado h1, .portal-invitado h2 {
    font-weight: 800;
    letter-spacing: -0.01em;
    margin: 0;
  }
  .portal-invitado .loading-screen {
    display: flex; align-items: center; justify-content: center; height: 100vh;
    color: var(--ink-soft); font-weight: 700;
  }
  .portal-invitado .label {
    text-transform: uppercase;
    letter-spacing: 0.09em;
    font-size: 0.72rem;
    font-weight: 800;
    color: var(--ink-soft);
  }
  .portal-invitado .topbar {
    background: #fff;
    border-bottom: 1px solid var(--line);
    padding: 12px 32px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .portal-invitado .brand { display: flex; align-items: center; gap: 12px; }
  .portal-invitado .brand img { height: 34px; display: block; }
  .portal-invitado .brand .divider { width: 1px; height: 26px; background: var(--line); }
  .portal-invitado .brand .portal-tag {
    font-size: 0.72rem; font-weight: 800; text-transform: uppercase;
    letter-spacing: 0.08em; color: var(--ink-soft);
  }
  .portal-invitado .topbar-right {
    display: flex; align-items: center; gap: 16px; font-size: 0.88rem; color: var(--ink-soft);
  }
  .portal-invitado .avatar {
    width: 34px; height: 34px; border-radius: 50%; background: var(--ink);
    display: flex; align-items: center; justify-content: center;
    font-weight: 800; font-size: 0.8rem; color: #fff;
  }
  .portal-invitado .hero {
    background: var(--ink); color: #fff; padding: 40px 32px 56px;
    position: relative; overflow: hidden;
  }
  .portal-invitado .hero::before {
    content: ""; position: absolute; right: -60px; top: -60px; width: 260px; height: 260px;
    background:
      radial-gradient(circle at 30% 30%, var(--blue) 0%, transparent 55%),
      radial-gradient(circle at 65% 55%, var(--green) 0%, transparent 55%),
      radial-gradient(circle at 45% 75%, var(--red) 0%, transparent 55%);
    opacity: 0.35; filter: blur(6px);
  }
  .portal-invitado .hero-inner { max-width: 1080px; margin: 0 auto; position: relative; }
  .portal-invitado .hero .label { color: #9fb3bd; }
  .portal-invitado .hero h1 { font-size: clamp(1.6rem, 2.6vw, 2.15rem); margin-top: 6px; color: #fff; }
  .portal-invitado .hero p.sub { margin: 10px 0 0; color: #c3d2d8; font-size: 0.98rem; max-width: 58ch; }
  .portal-invitado .content { max-width: 1080px; margin: -30px auto 60px; padding: 0 32px; }
  .portal-invitado .status-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 18px; }
  .portal-invitado .status-card {
    background: var(--paper-card); border-radius: var(--radius); box-shadow: var(--shadow);
    padding: 22px 20px; border: 1px solid var(--line); border-top: 3px solid var(--card-accent, var(--blue));
  }
  .portal-invitado .status-card.c-blue { --card-accent: var(--blue); }
  .portal-invitado .status-card.c-green { --card-accent: var(--green); }
  .portal-invitado .status-card.c-red { --card-accent: var(--red); }
  .portal-invitado .status-card .top-row { display: flex; align-items: flex-start; justify-content: space-between; }
  .portal-invitado .status-card .icon {
    width: 38px; height: 38px; border-radius: 10px;
    display: flex; align-items: center; justify-content: center; font-size: 1.1rem;
  }
  .portal-invitado .icon.blue { background: var(--blue-100); }
  .portal-invitado .icon.green { background: var(--green-100); }
  .portal-invitado .icon.red { background: var(--red-100); }
  .portal-invitado .status-card .figure {
    font-size: 2rem; font-weight: 800; margin-top: 14px; letter-spacing: -0.02em;
    font-variant-numeric: tabular-nums;
  }
  .portal-invitado .status-card .figure small { font-size: 1rem; font-weight: 600; color: var(--ink-soft); }
  .portal-invitado .status-card .caption { margin-top: 4px; font-size: 0.85rem; color: var(--ink-soft); font-weight: 600; }
  .portal-invitado .pill {
    font-size: 0.66rem; font-weight: 800; padding: 3px 9px; border-radius: 100px;
    text-transform: uppercase; letter-spacing: 0.04em;
  }
  .portal-invitado .pill.warn { background: var(--red-100); color: var(--red-dark); }
  .portal-invitado .pill.ok { background: var(--green-100); color: var(--green-dark); }
  .portal-invitado .pill.info { background: var(--blue-100); color: var(--blue-dark); }
  .portal-invitado .bar-track { margin-top: 12px; height: 6px; border-radius: 100px; background: var(--line); overflow: hidden; }
  .portal-invitado .bar-fill { height: 100%; border-radius: 100px; }
  .portal-invitado .bar-fill.blue { background: var(--blue); }
  .portal-invitado .bar-fill.green { background: var(--green); }
  .portal-invitado .bar-fill.red { background: var(--red); }
  .portal-invitado .split { margin-top: 26px; display: grid; grid-template-columns: 1.4fr 1fr; gap: 20px; }
  .portal-invitado .panel {
    background: var(--paper-card); border-radius: var(--radius); box-shadow: var(--shadow);
    border: 1px solid var(--line); padding: 24px;
  }
  .portal-invitado .panel-head { display: flex; align-items: baseline; justify-content: space-between; margin-bottom: 16px; }
  .portal-invitado .panel-head h2 { font-size: 1.1rem; }
  .portal-invitado .link { font-size: 0.82rem; font-weight: 700; color: var(--blue-dark); text-decoration: none; }
  .portal-invitado .checklist { display: flex; flex-direction: column; gap: 2px; }
  .portal-invitado .check-row { display: flex; align-items: center; gap: 12px; padding: 12px 4px; border-bottom: 1px solid var(--line); }
  .portal-invitado .check-row:last-child { border-bottom: none; }
  .portal-invitado .check-dot {
    width: 22px; height: 22px; border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    font-size: 0.72rem; font-weight: 800; flex-shrink: 0;
  }
  .portal-invitado .check-dot.done { background: var(--green); color: #fff; }
  .portal-invitado .check-dot.pending { background: #fff; border: 2px solid var(--red); color: var(--red); }
  .portal-invitado .check-text { flex: 1; }
  .portal-invitado .check-text .title { font-weight: 700; font-size: 0.93rem; }
  .portal-invitado .check-text .meta { font-size: 0.8rem; color: var(--ink-soft); margin-top: 1px; }
  .portal-invitado .action-list { display: flex; flex-direction: column; gap: 10px; }
  .portal-invitado .action {
    display: flex; align-items: center; gap: 12px; padding: 13px 14px; border-radius: 10px;
    border: 1px solid var(--line); background: var(--paper); text-decoration: none; color: var(--ink);
    transition: border-color 0.15s ease, background 0.15s ease;
  }
  .portal-invitado .action:hover { border-color: var(--blue); background: var(--blue-100); }
  .portal-invitado .action .glyph {
    width: 32px; height: 32px; border-radius: 8px; background: var(--ink); color: #fff;
    display: flex; align-items: center; justify-content: center; font-size: 0.95rem; flex-shrink: 0;
  }
  .portal-invitado .action .txt { font-weight: 700; font-size: 0.9rem; }
  .portal-invitado .action .arrow { margin-left: auto; color: var(--ink-soft); }
  .portal-invitado .inspection-banner {
    margin-top: 20px; border-radius: var(--radius); background: var(--ink); color: #fff;
    padding: 22px 26px; display: flex; align-items: center; justify-content: space-between; gap: 20px;
  }
  .portal-invitado .inspection-banner .date-block {
    text-align: center; background: rgba(255,255,255,0.09); border-radius: 10px; padding: 10px 16px; min-width: 64px;
  }
  .portal-invitado .inspection-banner .date-block .day { font-size: 1.5rem; font-weight: 800; line-height: 1; }
  .portal-invitado .inspection-banner .date-block .mon { font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.06em; color: #9fb3bd; }
  .portal-invitado .inspection-banner .btn {
    background: var(--blue); color: #06212e; border: none; padding: 10px 18px; border-radius: 8px;
    font-weight: 800; font-size: 0.85rem; cursor: pointer; white-space: nowrap; text-decoration: none;
  }
  .portal-invitado footer { text-align: center; padding: 30px 20px 50px; color: var(--ink-soft); font-size: 0.8rem; }
  @media (max-width: 860px) {
    .portal-invitado .status-grid { grid-template-columns: repeat(2, 1fr); }
    .portal-invitado .split { grid-template-columns: 1fr; }
    .portal-invitado .inspection-banner { flex-direction: column; align-items: flex-start; }
  }
  @media (prefers-color-scheme: dark) {
    .portal-invitado { background: #101113; color: #ecebe8; }
    .portal-invitado .topbar { background: #17181b; border-color: #2a2b2f; }
    .portal-invitado .status-card, .portal-invitado .panel { background: #17181b; border-color: #2a2b2f; }
    .portal-invitado .check-row { border-color: #2a2b2f; }
    .portal-invitado .action { background: #1d1e22; border-color: #2a2b2f; color: #ecebe8; }
    .portal-invitado .action:hover { background: #16323f; }
    .portal-invitado .bar-track { background: #2a2b2f; }
    .portal-invitado footer { color: #86898f; }
  }
`;

export default PortalInvitado;
